import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChartColumn,
    faTableList,
    faUserPen,
    faShareNodes,
    faDownload,
    faLock,
    faTh,
    faList,
    faPlus,
    faWandMagicSparkles,
    faMagnifyingGlass,
    faTriangleExclamation,
    faFileCsv,
} from '@fortawesome/free-solid-svg-icons';
import Layout from '../../components/Layout';
import Footer from '../../components/ui/Footer';
import { useFlashMessage } from '../../components/FlashMessage';
import withAuth from '../../utils/withAuth';
import imtLogo from '../../assets/images/IMT_logo.png';
import { extractApiError } from '../../utils/errorMessages';
import { fetchReportingOverview, copyReport } from '../../services/reportingService';
import ReportViewer from './ReportViewer';
import ReportBuilder from './ReportBuilder';
import AskAi from './AskAi';
import AccountingExport from './AccountingExport';
import './ReportingCentral.css';
// Reusable Notification-Center-style tab bar (.hms-tabs / .tab-button / .tab-icon).
import '../../components/ui/HMSTabs.css';

const TABS = [
    { value: 'standard', label: 'Standard reports', icon: faTableList },
    { value: 'ask', label: 'Ask AI', icon: faWandMagicSparkles },
    { value: 'mine', label: 'My reports', icon: faUserPen },
];
// Shown only when the server says this person has something here: a company whose
// books they may export to a file, or a past export to read again (finance right).
const EXPORT_TAB = { value: 'export', label: 'Accounting export', icon: faFileCsv };

/**
 * Reporting Central (ledger row 123) — the standard report formats and a
 * person's own reports in one place.
 *
 * The server decides everything that matters: which reports are listed, whether
 * this person may run one, and the sentence explaining why not when they may
 * not. This screen never re-derives any of that; a card with a `locked_reason`
 * simply shows the sentence and refuses to open.
 */
const ReportingCentral = () => {
    const { showError } = useFlashMessage();
    const [overview, setOverview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [activeTab, setActiveTab] = useState('standard');
    const [search, setSearch] = useState('');
    const [viewMode, setViewMode] = useState('grid');
    // The viewer and the builder open IN PLACE, not on their own address — the
    // breadcrumb goes back. `builder` holds { report, seed }: a saved card being
    // edited, or a recipe Ask AI wrote that has never been saved.
    const [openReport, setOpenReport] = useState(null);
    const [builder, setBuilder] = useState(null);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const loadOverview = useCallback(async ({ silent = false } = {}) => {
        if (!silent) setLoading(true);
        setError('');
        try {
            const data = await fetchReportingOverview();
            setOverview(data);
        } catch (err) {
            if (!silent) setError(extractApiError(err, 'Could not load Reporting Central. Please try again.'));
        } finally {
            if (!silent) setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadOverview();
    }, [loadOverview]);

    const counts = overview?.counts || {};
    const tabs = overview?.accounting_export_offered ? [...TABS, EXPORT_TAB] : TABS;

    const visibleReports = useMemo(() => {
        const query = search.trim().toLowerCase();
        return (overview?.reports || []).filter((report) => {
            const inTab = activeTab === 'standard'
                ? report.kind === 'standard'
                : report.kind === 'mine' || report.kind === 'shared';
            if (!inTab) return false;
            if (!query) return true;
            return (report.name || '').toLowerCase().includes(query)
                || (report.description || '').toLowerCase().includes(query);
        });
    }, [overview, activeTab, search]);

    const handleOpen = (report) => {
        if (report.locked_reason) {
            showError(report.locked_reason, 7000);
            return;
        }
        setOpenReport(report);
    };

    const refreshInBackground = () => loadOverview({ silent: true });

    const handleEdit = (report) => {
        setOpenReport(null);
        setBuilder({ report, seed: null });
    };

    // A copy belongs to the person who made it, so the server makes it first and
    // the builder then opens the real saved report rather than a pretend one.
    const handleCopy = async (report) => {
        try {
            const card = await copyReport(report.id);
            refreshInBackground();
            setOpenReport(null);
            setActiveTab('mine');
            setBuilder({ report: card, seed: null });
        } catch (err) {
            showError(extractApiError(err, 'Could not copy this report to My reports.'));
        }
    };

    const renderCard = (report) => {
        const blocked = Boolean(report.locked_reason);
        return (
            <div
                key={report.id}
                role={blocked ? undefined : 'button'}
                tabIndex={blocked ? undefined : 0}
                className={`rc-card ${blocked ? 'rc-card-blocked' : ''}`}
                onClick={blocked ? undefined : () => handleOpen(report)}
                onKeyDown={blocked ? undefined : (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleOpen(report);
                    }
                }}
            >
                <div className="rc-card-top">
                    <div className="rc-card-icon">
                        <FontAwesomeIcon icon={blocked ? faLock : faChartColumn} />
                    </div>
                    {report.kind === 'standard' ? (
                        <span className="rc-pill">Standard · locked</span>
                    ) : null}
                    {report.is_shared && report.kind === 'shared' ? (
                        <span className="rc-pill rc-pill-shared">
                            <FontAwesomeIcon icon={faShareNodes} /> Shared
                        </span>
                    ) : null}
                </div>
                <div className="rc-card-name">{report.name}</div>
                {report.description ? (
                    <p className="rc-card-desc">{report.description}</p>
                ) : null}
                <div className="rc-card-meta">
                    {report.column_count} columns · {report.subject_label}
                </div>
                {blocked ? (
                    <div className="rc-card-locked">
                        <FontAwesomeIcon icon={faLock} /> {report.locked_reason}
                    </div>
                ) : null}
            </div>
        );
    };

    const activeTabLabel = tabs.find((tab) => tab.value === activeTab)?.label || '';

    return (
        <Layout showLeftNavSidebar showRightSidebar={false}>
            <div className="rc-hub">
                <div className="rc-header">
                    <div className="rc-title-section">
                        <img src={imtLogo} alt="IMT" className="rc-header-logo-img" />
                        <div>
                            <h1 className="hms-screen-title">Reporting Central</h1>
                            <p className="rc-subtitle">
                                Standard formats and your own reports in one place
                            </p>
                        </div>
                    </div>
                </div>

                {/* Summary strip */}
                <div className="rc-stats-row">
                    <div className="rc-stat-card rc-stat-standard">
                        <div className="rc-stat-icon"><FontAwesomeIcon icon={faTableList} /></div>
                        <div>
                            <div className="rc-stat-value">{loading ? '…' : counts.standard ?? 0}</div>
                            <div className="rc-stat-label">Standard reports</div>
                        </div>
                    </div>
                    <div className="rc-stat-card rc-stat-mine">
                        <div className="rc-stat-icon"><FontAwesomeIcon icon={faUserPen} /></div>
                        <div>
                            <div className="rc-stat-value">{loading ? '…' : counts.mine ?? 0}</div>
                            <div className="rc-stat-label">My reports</div>
                        </div>
                    </div>
                    <div className="rc-stat-card rc-stat-shared">
                        <div className="rc-stat-icon"><FontAwesomeIcon icon={faShareNodes} /></div>
                        <div>
                            <div className="rc-stat-value">{loading ? '…' : counts.shared ?? 0}</div>
                            <div className="rc-stat-label">Shared with me</div>
                        </div>
                    </div>
                    <div className="rc-stat-card rc-stat-downloads">
                        <div className="rc-stat-icon"><FontAwesomeIcon icon={faDownload} /></div>
                        <div>
                            <div className="rc-stat-value">
                                {loading ? '…' : counts.downloads_this_month ?? 0}
                            </div>
                            <div className="rc-stat-label">Downloads this month</div>
                        </div>
                    </div>
                </div>

                {!openReport && !builder && (
                    <div className="hms-tabs">
                        <div className="tabs-container">
                            {tabs.map((tab) => (
                                <button
                                    key={tab.value}
                                    type="button"
                                    className={`tab-button ${activeTab === tab.value ? 'active' : ''}`}
                                    onClick={() => setActiveTab(tab.value)}
                                >
                                    <div className="tab-icon">
                                        <FontAwesomeIcon icon={tab.icon} />
                                    </div>
                                    <span>{tab.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                <div className="rc-content">
                    {builder ? (
                        <ReportBuilder
                            report={builder.report}
                            seed={builder.seed}
                            canLock={Boolean(overview?.can_lock)}
                            onBack={() => setBuilder(null)}
                            onSaved={refreshInBackground}
                        />
                    ) : openReport ? (
                        <ReportViewer
                            report={openReport}
                            breadcrumbRoot={activeTabLabel}
                            onBack={() => setOpenReport(null)}
                            canBuild={Boolean(overview?.can_build)}
                            onEdit={handleEdit}
                            onCopy={handleCopy}
                            onDeleted={refreshInBackground}
                        />
                    ) : activeTab === 'ask' ? (
                        <AskAi
                            canBuild={Boolean(overview?.can_build)}
                            onOpenInBuilder={(seed) => setBuilder({ report: null, seed })}
                            onSaved={refreshInBackground}
                        />
                    ) : activeTab === 'export' && overview?.accounting_export_offered ? (
                        <AccountingExport />
                    ) : loading ? (
                        <div className="rc-loading">Loading Reporting Central…</div>
                    ) : error ? (
                        <div className="rc-panel">
                            <div className="rc-error">
                                <FontAwesomeIcon icon={faTriangleExclamation} /> {error}
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="rc-toolbar">
                                <div className="rc-toolbar-left">
                                    <div className="rc-search">
                                        <FontAwesomeIcon icon={faMagnifyingGlass} className="rc-search-icon" />
                                        <input
                                            type="text"
                                            aria-label="Search reports"
                                            placeholder="Search reports…"
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="rc-toolbar-right">
                                    {overview?.can_build && (
                                        <button
                                            type="button"
                                            className="rc-btn-new"
                                            onClick={() => setBuilder({ report: null, seed: null })}
                                        >
                                            <FontAwesomeIcon icon={faPlus} /> New report
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        className="rc-view-toggle"
                                        onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
                                        title={viewMode === 'grid' ? 'List view' : 'Grid view'}
                                    >
                                        <FontAwesomeIcon icon={viewMode === 'grid' ? faList : faTh} />
                                    </button>
                                </div>
                            </div>

                            {visibleReports.length === 0 ? (
                                <div className="rc-panel">
                                    <div className="rc-empty">
                                        <FontAwesomeIcon icon={faChartColumn} className="rc-empty-icon" />
                                        <div className="rc-empty-title">No reports to show</div>
                                        <p>
                                            {search.trim()
                                                ? 'No reports match your search in this tab.'
                                                : activeTab === 'mine'
                                                    ? 'Reports you build or save will appear here.'
                                                    : 'No standard reports have been registered yet.'}
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <div className={viewMode === 'grid' ? 'rc-card-grid' : 'rc-card-list'}>
                                    {visibleReports.map(renderCard)}
                                </div>
                            )}
                        </>
                    )}
                </div>

                <Footer />
            </div>
        </Layout>
    );
};

export default withAuth(ReportingCentral);
